import React from "react";
import { type DatepickerProps, SupDatepicker, SupFieldControl } from "@repo/core-ui";
import { max, isAfter } from "date-fns";

import { FieldProps } from "../types";
import { useField } from "../hooks";

type FormattedDate = Date | string;

export interface DatepickerFieldProps
  extends Omit<FieldProps, "defaultValue">,
    Pick<DatepickerProps, "defaultOpen" | "format" | "displayTimePicker" | "minDate" | "maxDate" | "exclude" | "minTime" | "maxTime"> {
  defaultValue?: Date | string;
  formatValue?: (value: Date) => FormattedDate;
  parseValue?: (formattedValue: string) => Date;
}

const MIN_DATE = new Date(100, 0, 1);

const defaultValueFormatter = (value: Date) => value;
const defaultValueParser = (value: FormattedDate) => value as Date;

export function SupDatepickerField({
  name,
  id = name,
  required,
  label,
  hint,
  hintAlign,
  prepend,
  className,
  controlClassName,
  fluid,
  disabled,
  autoFocus,
  color,
  defaultValue,
  placeholder,
  shouldUnregister,
  icon,
  formatValue = defaultValueFormatter,
  parseValue = defaultValueParser,
  ...datePickerProps
}: DatepickerFieldProps) {
  const { value, error, invalid, onChange, onBlur } = useField({ name, defaultValue, autoFocus, shouldUnregister });
  const parsedValue = typeof value === "string" ? parseValue(value) : value;
  const fieldColor = invalid ? "error" : color;

  function handleChange(value?: Date) {
    if (value && isAfter(value, MIN_DATE)) {
      onChange(formatValue(value));
    } else {
      onChange(null);
    }
  }

  return (
    <SupFieldControl
      disabled={disabled}
      fluid={fluid}
      required={required}
      id={id}
      label={label}
      hint={invalid ? error : hint}
      hintAlign={hintAlign}
      prepend={prepend}
      className={controlClassName}
      color={fieldColor}
      icon={icon}
    >
      <SupDatepicker
        // ref={ref}
        id={id}
        name={name}
        className={className}
        fluid={fluid}
        color={fieldColor}
        disabled={disabled}
        placeholder={placeholder}
        value={isAfter(parsedValue, MIN_DATE) ? parsedValue : undefined}
        {...datePickerProps}
        minDate={datePickerProps.minDate ? max([MIN_DATE, datePickerProps.minDate]) : MIN_DATE}
        onChange={handleChange}
        onBlur={onBlur}
      />
    </SupFieldControl>
  );
}
